import { useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import { isProvinciaSlug } from "../utils/provincias";
import { trackEvent, EVENTS } from "../lib/analytics";

/**
 * Botón flotante WhatsApp — visible en móvil, tablet y escritorio.
 *
 * Reglas:
 *  - Presente en todas las páginas públicas.
 *  - Oculto en /admin/* y /login.
 *  - En la ficha individual de subasta (móvil) se oculta solo en < md porque
 *    ya existe la barra sticky con WhatsApp propio — en tablet/desktop sí
 *    aparece, dado que en esos tamaños no se muestra la sticky.
 *  - z-index 90: por debajo del banner de cookies (z-100) para que el aviso
 *    RGPD siempre sea prioritario hasta que el usuario lo acepte/rechace.
 */
export default function FloatingWhatsApp() {
  const { pathname } = useLocation();

  if (pathname.startsWith("/admin")) return null;
  if (pathname === "/login") return null;

  // Detectar ficha individual de subasta (no listado de provincia).
  const twoSegs = /^\/subastas\/[^/]+\/[^/]+/.test(pathname);
  const oneSeg = pathname.match(/^\/subastas\/([^/]+)$/);
  const isSubastaDetail =
    twoSegs || (oneSeg && !isProvinciaSlug(oneSeg[1]));

  // En ficha de subasta: oculto solo en móvil (< md); visible en tablet/desktop.
  // En cualquier otra página: visible en TODOS los tamaños.
  const visibilityClass = isSubastaDetail ? "hidden md:flex" : "flex";

  const msg = WA_MSG.generic;

  return (
    <a
      href={buildWaUrl(msg)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        trackEvent(EVENTS.CTA_WHATSAPP, {
          source: isSubastaDetail ? "floating_subasta_desktop" : "floating",
        });
        openWhatsApp(msg);
      }}
      aria-label="Contactar por WhatsApp"
      data-testid="floating-whatsapp-btn"
      className={
        `${visibilityClass} ` +
        // Posición: esquina inferior derecha con separación adecuada.
        `fixed bottom-5 right-4 md:bottom-6 md:right-6 lg:bottom-7 lg:right-7 z-[90] ` +
        // Tamaño responsive.
        `w-14 h-14 md:w-16 md:h-16 ` +
        // Estilo (verde WhatsApp corporativo + sombra suave).
        `rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white ` +
        `shadow-[0_8px_24px_rgba(37,211,102,0.45)] ` +
        // Layout icono centrado + interacción.
        `items-center justify-center transition-transform active:scale-95 hover:scale-105 ` +
        `focus:outline-none focus:ring-2 focus:ring-white/70`
      }
    >
      <span
        className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping pointer-events-none"
        aria-hidden="true"
      />
      <MessageCircle className="w-7 h-7 md:w-8 md:h-8 relative" strokeWidth={2.2} />
    </a>
  );
}
